import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Cookie from '@wetransfer/concorde-cookie';

import { connect } from 'react-redux';

import { AppConfig } from '../../core-bundle/config/app-config';

import Config from './config';

import NavigationItem from './navigation-item';
import SubNavigation from './sub-navigation';

import { isCurrentPath } from './navigation-item/helpers/navigation-item';

import Router from '@module/router';
import I18n from '@module/i18n';

import Actions from '@app/store/action';

import Poptip from '@component/info/poptip';

let NAV_LOADED = false;
const POPTIP_ANIMATION = {
  show: {
    properties: { opacity: [1, 0] },
    options: { duration: 300, easing: [0.55, 0.86, 0.69, 0.53] },
  },
  hide: {
    properties: { opacity: 0 },
    options: { duration: 300, easing: [0.55, 0.86, 0.69, 0.53] },
  },
};
const BASE_CLASS_NAME = 'nav';
const ITEM_BASE_CLASS_NAME = `${BASE_CLASS_NAME}__item`;
const SUB_ITEM_BASE_CLASS_NAME = `${BASE_CLASS_NAME}__subitem`;

export class Navigation extends React.Component {
  componentDidMount() {
    // Because otherwise the fade-in animation happens
    // every time you open/close panel.
    this.fadeInTimeout = setTimeout(() => {
      NAV_LOADED = true;
    }, 300);

    this.startNotification();
  }

  componentWillUnmount() {
    clearTimeout(this.fadeInTimeout);
    clearTimeout(this.onboardingNotificationTimeout);
    clearTimeout(this.signinNotificationTimeout);
    clearTimeout(this.tooltipTimeout);
  }

  startNotification = () => {
    const { route, user, systemNotification } = this.props;
    const notifications = Config.getNotifications();
    const showOnboarding =
      route.query.claimedDomain &&
      !Cookie.get(`wt_${notifications.onboarding.name}`);
    const showSignin =
      user.loggedIn &&
      route.type === 'transfer' &&
      !Cookie.get(`wt_${notifications.signin.name}`);

    //return in case a notification is displayed next to the logo.
    if (systemNotification) {
      return;
    }

    if (showOnboarding) {
      this.onboardingNotificationTimeout = setTimeout(
        () => this.showNotification(notifications.onboarding.name),
        AppConfig.navigation.tooltip.showDelay
      );
    } else if (showSignin) {
      this.signinNotificationTimeout = setTimeout(
        () => this.showNotification(notifications.signin.name),
        AppConfig.navigation.tooltip.showDelay
      );
    }
  };

  showNotification = (details) => {
    this.props.setPopoverNotification('notification', details);
    // Set a cookie to prevent showing the tooltip again in the coming 24h
    Cookie.set(`wt_${details}`, 1, { days: 1 });
    // Hide tooltip after a certain delay
    this.tooltipTimeout = setTimeout(
      this.props.resetPopoverNotification,
      AppConfig.navigation.tooltip.hideDelay[details]
    );
  };

  getNotification(menuItem) {
    const { systemNotification, notification } = this.props;

    if (!systemNotification) {
      return false;
    }

    if (!Object.keys(notification.popover).length) {
      return false;
    }

    if (
      !menuItem.notificationDetails ||
      menuItem.notificationDetails.length === 0
    ) {
      return false;
    }

    if (!menuItem.notificationDetails.includes(notification.popover.details)) {
      return false;
    }

    const translationKey = `notification.${notification.popover.details}`;

    return I18n.t(translationKey) || notification.panel.details;
  }

  render() {
    const { route, layout, resetPopoverNotification } = this.props;
    const currentRouter = Router.currentRouter;

    let items = Config.getItems();
    const className = classnames(BASE_CLASS_NAME, {
      [`${BASE_CLASS_NAME}--with-panel`]: route.type !== 'transfer',
      [`${BASE_CLASS_NAME}--loaded`]: NAV_LOADED,
      [`${BASE_CLASS_NAME}--html5-fullscreen`]: layout.hidden === true,
    });

    // Render a single back button item in the nav
    if (currentRouter) {
      const lastPathOptions = currentRouter.lastPathOptions;
      if (lastPathOptions.backTo) {
        items = Config.getBackButtonItem(
          lastPathOptions.backButtonText,
          lastPathOptions.backTo
        );
      }
    }

    const navigationItems = items.map((item) => {
      let hasActiveChild = false;
      let { path } = item;

      const subItems = (item.items || []).map((subItem) => {
        const isActiveChild = isCurrentPath(route.path, subItem.path);
        hasActiveChild = hasActiveChild || isActiveChild;
        path = path || subItem.path;

        const subItemClassName = classnames(SUB_ITEM_BASE_CLASS_NAME, {
          [`${SUB_ITEM_BASE_CLASS_NAME}--active`]: isActiveChild,
          [`${ITEM_BASE_CLASS_NAME}--active`]: isActiveChild,
        });

        return (
          <li key={subItem.name} className={subItemClassName}>
            <NavigationItem
              isActive={isActiveChild}
              preventSelfClose
              {...subItem}
            />
          </li>
        );
      });

      const isActive = hasActiveChild || isCurrentPath(route.path, path);

      const navigationItem = (
        <NavigationItem
          isActive={isActive}
          onClick={resetPopoverNotification}
          path={path}
          {...item}
        >
          <SubNavigation hasActiveChild={hasActiveChild}>
            {subItems}
          </SubNavigation>
        </NavigationItem>
      );

      const navigationItemClassName = classnames(ITEM_BASE_CLASS_NAME, {
        [`${ITEM_BASE_CLASS_NAME}--active`]: isActive,
      });

      const notification = this.getNotification(item);

      return (
        <li key={item.name} className={navigationItemClassName}>
          {notification ? (
            <Poptip
              className="nav__item-tooltip"
              effects={POPTIP_ANIMATION}
              offset={12}
              autoHide={true}
              visible={true}
              trigger="manual"
              anchor="bottom-left"
              content={<span>{notification}</span>}
            >
              {navigationItem}
            </Poptip>
          ) : (
            navigationItem
          )}
        </li>
      );
    });

    return (
      <nav className={className}>
        <ul className="nav__items">{navigationItems}</ul>
      </nav>
    );
  }
}

Navigation.propTypes = {
  layout: PropTypes.object.isRequired,
  notification: PropTypes.object.isRequired,
  resetPopoverNotification: PropTypes.func.isRequired,
  route: PropTypes.object.isRequired,
  setPopoverNotification: PropTypes.func.isRequired,
  systemNotification: PropTypes.string,
  user: PropTypes.object.isRequired,
};

const mapStateToProps = ({ layout, notification, route, user }) => ({
  layout,
  notification,
  route,
  user,
});
export default connect(mapStateToProps, Actions.Notification)(Navigation);
